import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

interface ResourceType {
  id: string | number;
  name: string;
  attributes?: ResourceAttribute[];
  capacity?: number;
  [key: string]: any;
}

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  external: boolean;
  resourceTypeId: string;
  order: number | null;
  selectDisplayValues?: string | null;
  selectSlugValues?: string | null;
}

interface ResourceAttributeValue {
  resourceAttributeId: string;
  resourceId: string;
  value: string;
}

@Component({
  selector: 'app-resource-dialog',
  templateUrl: './resource-dialog.component.html',
  styleUrls: ['./resource-dialog.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatDialogModule,
  ],
})
export class ResourceDialogComponent implements OnInit {
  attributeValues: { [key: string]: string } = {};

  // Cache select options for each attribute by id
  selectOptionsCache: { [attributeId: string]: { slug: string; display: string }[] } = {};
  
  constructor(
    public dialogRef: MatDialogRef<ResourceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      id?: string;
      name: string;
      external?: boolean;
      capacity?: number;
      resourceTypeId: string;
      resourceTypes: ResourceType[];
      resourceType?: ResourceType;
      attributeValues?: ResourceAttributeValue[];
      [key: string]: any;
    }
  ) {}
  
  ngOnInit() {
    // Initialize capacity if not set
    if (!this.data.capacity) {
      this.data.capacity = 1;
    }
    
    // Set max capacity based on resource type's maxConcurrent
    this.updateCapacityConstraints();
    
    // Initialize attribute values from existing data
    if (this.data.attributeValues && this.data.attributeValues.length > 0) {
      console.log('Initializing attribute values:', this.data.attributeValues);
      this.data.attributeValues.forEach(attrValue => {
        this.attributeValues[attrValue.resourceAttributeId] = attrValue.value;
      });
      console.log('Attribute values initialized:', this.attributeValues);
    } else {
      console.log('No attribute values to initialize');
      
      // Initialize empty values for all attributes
      if (this.data.resourceType && this.data.resourceType.attributes) {
        this.data.resourceType.attributes.forEach(attr => {
          // Set default values based on attribute type
          if (attr.type === 'checkbox') {
            this.attributeValues[attr.id] = 'false';
          } else if (attr.type === 'number') {
            this.attributeValues[attr.id] = '0';
          } else {
            this.attributeValues[attr.id] = '';
          }
        });
        console.log('Default attribute values initialized:', this.attributeValues);
      }
    }

    // Precompute select options for all select attributes
    const attributes = this.getResourceTypeAttributes();
    attributes.forEach(attr => {
      if (attr.type === 'select') {
        this.selectOptionsCache[attr.id] = this.computeSelectOptions(attr);
      }
    });
  }
  
  getResourceTypeName(): string {
    if (!this.data.resourceTypeId) return '';
    
    const resourceTypeId = this.data.resourceTypeId.toString();
    const resourceType = this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
    return resourceType ? resourceType.name : '';
  }
  
  resourceTypeHasAttributes(): boolean {
    return this.getResourceTypeAttributes().length > 0;
  }
  
  getResourceTypeAttributes(): ResourceAttribute[] {
    if (!this.data.resourceTypeId) return [];
    
    const resourceTypeId = this.data.resourceTypeId.toString();
    const resourceType = this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
    
    // Return attributes sorted by order
    if (resourceType && resourceType.attributes) {
      return [...resourceType.attributes].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return [];
  }

  updateCapacityConstraints() {
    const resourceType = this.getResourceType();
    if (resourceType && resourceType['maxConcurrent'] !== null && resourceType['maxConcurrent'] !== undefined) {
      // If capacity exceeds maxConcurrent, reset it
      if (this.data.capacity && this.data.capacity > resourceType['maxConcurrent']) {
        this.data.capacity = resourceType['maxConcurrent'];
      }
    }
  }
  
  getResourceType(): ResourceType | undefined {
    if (!this.data.resourceTypeId) return undefined;
    const resourceTypeId = this.data.resourceTypeId.toString();
    return this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
  }
  
  getMaxCapacity(): number | null {
    const resourceType = this.getResourceType();
    return resourceType?.['maxConcurrent'] || null;
  }

  onSubmit() {
    if (this.data.name && this.data.resourceTypeId) {
      // Validate capacity against resource type's maxConcurrent
      const resourceType = this.getResourceType();
      if (resourceType && resourceType['maxConcurrent'] !== null && resourceType['maxConcurrent'] !== undefined) {
        if (this.data.capacity && this.data.capacity > resourceType['maxConcurrent']) {
          return; // Don't submit if validation fails
        }
      }
      
      // Convert attribute values to array format
      const attributeValues: ResourceAttributeValue[] = [];
      
      for (const [attributeId, value] of Object.entries(this.attributeValues)) {
        attributeValues.push({
          resourceAttributeId: attributeId,
          resourceId: this.data.id || '',
          value: value.toString() // Convert any non-string values to string
        });
      }
      
      this.dialogRef.close({
        ...this.data,
        attributeValues
      });
    }
  }

  /**
   * For select attributes, return an array of {slug, display} options.
   * This is now cached in selectOptionsCache.
   */
  getSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    return this.selectOptionsCache[attribute.id] || [];
  }

  private computeSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    if (!attribute || attribute.type !== 'select') return [];
    if (!attribute.selectDisplayValues || !attribute.selectSlugValues) return [];
    const displayArr = attribute.selectDisplayValues.split(',').map(s => s.trim());
    const slugArr = attribute.selectSlugValues.split(',').map(s => s.trim());
    return slugArr.map((slug, idx) => ({
      slug,
      display: displayArr[idx] !== undefined ? displayArr[idx] : slug
    }));
  }
}
